<template>
    <b-dropdown
        ref="timeRangeCalendar"
        variant="primary"
        right
        no-flip
        :disabled="disabled"
        menu-class="p-0"
    >

        <template #button-content>
            <span v-if="internalDates && internalDates.length === 2">
                {{ $format.date(internalDates[0]) }} – {{ $format.date(internalDates[1]) }}
            </span>
            <i class="icon">
                <feather type="chevron-down" class="align-middle" />
            </i>
        </template>

        <b-dropdown-form form-class="p-0">
            <div class="p-3" @click.stop @mousedown.stop>
                <FormPickerDate
                    v-model="internalDates"
                    range
                    :confirm="needConfirm"
                    inline
                    shortcuts
                    @input="onInput"
                    @confirm="onConfirm"
                    @cancel="onCancel"
                >

                    <template v-if="compareYear" #header>
                        <b-form-radio-group v-model="compareType" class="h-100 d-flex align-items-center">
                            <b-form-radio v-for="option in compareTypeOptions" :key="option.value" :value="option.value">
                                <span class="font-weight-normal">{{ option.text }}</span>
                            </b-form-radio>
                        </b-form-radio-group>
                    </template>
                </FormPickerDate>
            </div>
        </b-dropdown-form>

    </b-dropdown>
</template>



<script>
    import { DEFAULT_TIME_RANGE, AVAILABLE_OPTIONS, dateWithoutUTC, getTimeRangeCustom, TIME_RANGES } from '@/utils/timeRanges';
    import FormPickerDate from '@/components/common/FormPickerDate.vue';
    import { isMobile } from '@/utils/util';

    const COMPARE_TYPES = {
        DEFAULT: 'DEFAULT',
        YEAR: 'YEAR',
    };

    export default {
        name: 'TimeRangeCalendar',
        components: {
            FormPickerDate,
        },
        props: {
            value: {
                type: Object,
                default: () => {
                    const { startDate, endDate, compareStartDate, compareEndDate } = DEFAULT_TIME_RANGE;
                    return { startDate, endDate, compareStartDate, compareEndDate };
                },
            },
            options: {
                type: Array,
                default: () => [...AVAILABLE_OPTIONS],
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            compareYear: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                internalDates: this.buildInitialDates(this.value),
                compareType: COMPARE_TYPES.DEFAULT,
            };
        },
        computed: {
            needConfirm() {
                return !isMobile();
            },
            compareTypeOptions() {
                return [
                    { value: COMPARE_TYPES.DEFAULT, text: this.$t('compareTypeDefault') },
                    { value: COMPARE_TYPES.YEAR, text: this.$t('compareTypeYear') },
                ];
            },
        },
        watch: {
            value: {
                handler(newVal) {
                    const newDates = this.buildInitialDates(newVal);
                    // porównujemy po getTime(), żeby nie ustawiać zbędnie tej samej wartości
                    if (!this.internalDates || this.internalDates.length !== 2 || this.internalDates[0].getTime() !== newDates[0].getTime() || this.internalDates[1].getTime() !== newDates[1].getTime()) {
                        this.internalDates = newDates;
                    }
                },
                deep: true,
                immediate: true,
            },
        },
        methods: {
            buildInitialDates(valueObj) {
                let sd = valueObj?.startDate;
                let ed = valueObj?.endDate;
                if (!sd || !ed) {
                    sd = DEFAULT_TIME_RANGE.startDate;
                    ed = DEFAULT_TIME_RANGE.endDate;
                }
                return [dateWithoutUTC(sd), dateWithoutUTC(ed)];
            },
            onConfirm(pickerDates) {
                this.internalDates = pickerDates;
                this.emitData(this.internalDates, true);
                this.$refs.timeRangeCalendar.hide(true);
            },
            onInput(pickerDates) {
                if (!this.needConfirm) this.onConfirm(pickerDates);
            },
            onCancel() {
                this.internalDates = this.buildInitialDates(this.value);
                this.$refs.timeRangeCalendar.hide(true);
            },
            resetDate() {
                this.internalDates = this.buildInitialDates();
                this.emitData(this.internalDates, true);
                if (this.$refs.timeRangeCalendar) this.$refs.timeRangeCalendar.hide(true);
            },
            emitData(datesArray, change) {
                const timeRange = this.getTimeRange(datesArray);
                this.$emit('input', timeRange);
                this.$emit('startDate', timeRange.startDate);
                this.$emit('endDate', timeRange.endDate);
                this.$emit('timeRange', timeRange);
                if (change) {
                    this.$emit('change', timeRange);
                }
            },
            getTimeRange(dates) {
                const { startDate, endDate, compareStartDate, compareEndDate, compareYearStartDate, compareYearEndDate } = this.getTimeRangeFn(dates)(dates);
                if (this.compareType === COMPARE_TYPES.YEAR) return { startDate, endDate, compareStartDate: compareYearStartDate, compareEndDate: compareYearEndDate };
                return { startDate, endDate, compareStartDate, compareEndDate };
            },
            getTimeRangeFn(dates) {
                const custom = getTimeRangeCustom(dates);
                const keys = AVAILABLE_OPTIONS.filter(name => name !== 'custom');
                const name = keys.find(key => {
                    const { startDate, endDate } = TIME_RANGES[key]();
                    return custom.startDate === startDate && custom.endDate === endDate;
                });
                if (name) return TIME_RANGES[name];
                return getTimeRangeCustom;
            },
        },
        created() {
            if (!this.value?.startDate || !this.value?.endDate) {
                this.emitData(this.internalDates, false);
            }
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "compareTypeDefault": "Porównaj z poprzednim zakresem",
    "compareTypeYear": "Porównaj do ubiegłego roku"
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "compareTypeDefault": "Compare to previous range",
    "compareTypeYear": "Compare to last year"
}
</i18n>
